import { MULTIPART_UPLOAD_THRESHOLD_BYTES } from "@assetwatch/modality-api/motion-amplification/schemas";
import { toast } from "@assetwatch/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import {
  createClientId,
  createMotionAmplificationUpload,
  type MotionAmplificationJobId,
  type MotionAmplificationParameters,
  type MotionAmplificationPreset,
  type MotionAmplificationRawVideo,
  reportMotionAmplificationJobFailure,
  startMotionAmplificationJob,
  uploadMotionAmplificationVideo,
} from "../clients/motion-amplification";
import { S3UploadError } from "../clients/s3UploadError";
import { motionAmplificationJobsListKey } from "../queries/useMotionAmplificationJobsQuery";
import { motionAmplificationRawVideosQueryKey } from "../queries/useMotionAmplificationRawVideosQuery";
import type { MultipartUploadSession } from "./multipartUploadSession";
import {
  expirePausedMultipartUpload,
  showPausedMultipartUploadToast,
} from "./pausedMultipartUploadToast";
import {
  isRetryableUploadError,
  MultipartUploadPausedError,
  uploadMultipartMotionAmplificationVideo,
  waitBeforeRetry,
} from "./uploadMultipartMotionAmplificationVideo";

type MotionAmplificationSubmissionDetails = {
  name: string;
  description: string;
  preset: MotionAmplificationPreset;
  parameters: MotionAmplificationParameters;
  fps: number | null;
  width?: number | null;
  height?: number | null;
  machineId?: string | null;
  onUploadStarted?: (jobId: MotionAmplificationJobId) => void;
};

export type MotionAmplificationSubmission =
  | (MotionAmplificationSubmissionDetails & {
      file: File;
      fileHandle?: FileSystemFileHandle;
      resumeSession?: MultipartUploadSession;
      existingVideo: null;
    })
  | (MotionAmplificationSubmissionDetails & {
      file: null;
      fileHandle?: undefined;
      resumeSession?: undefined;
      existingVideo: MotionAmplificationRawVideo;
    });

/** Upload progress (0-100) by job; reaches 100 once the S3 PUT (or the "existing video" no-op) finishes. */
export type ActiveMotionAmplificationUploads = ReadonlyMap<
  MotionAmplificationJobId,
  number
>;

const MAX_SINGLE_UPLOAD_ATTEMPTS = 3;

// Multipart parts already retry; a single PUT needs the same tolerance for dropped connections.
async function retryTransientUploadFailures(
  jobId: MotionAmplificationJobId,
  upload: () => Promise<void>,
  signal: AbortSignal,
): Promise<void> {
  for (let attempt = 0; ; attempt++) {
    try {
      return await upload();
    } catch (error) {
      // Retries reuse one presigned URL, so an expired signature can't succeed.
      const urlExpired =
        error instanceof S3UploadError && error.httpStatus === 403;
      const willRetry =
        !signal.aborted &&
        !urlExpired &&
        isRetryableUploadError(error) &&
        attempt < MAX_SINGLE_UPLOAD_ATTEMPTS - 1;
      console.warn("motionAmplification.upload.attemptFailed", {
        jobId,
        attempt: attempt + 1,
        maxAttempts: MAX_SINGLE_UPLOAD_ATTEMPTS,
        willRetry,
        errorMessage: error instanceof Error ? error.message : undefined,
      });
      if (!willRetry) throw error;
      await waitBeforeRetry(attempt, signal);
    }
  }
}

const motionAmplificationUploadMutationKey = [
  "motion-amplification",
  "upload",
] as const;

/**
 * Orchestrates create -> S3 PUT -> start. Its mutation lifecycle refreshes
 * the jobs list on completion and raw videos only after a new file upload.
 * `activeUploads` tracks client-side S3 upload percentage per job, so several
 * uploads can run concurrently; a job stays listed until its jobs refresh lands.
 */
export function useUploadMotionAmplificationVideoMutation() {
  const queryClient = useQueryClient();
  const [activeUploads, setActiveUploads] =
    useState<ActiveMotionAmplificationUploads>(new Map());
  const isMounted = useRef(true);
  const uploadAbortControllers = useRef(
    new Map<MotionAmplificationJobId, AbortController>(),
  );
  const jobIdsBySubmission = useRef(
    new WeakMap<MotionAmplificationSubmission, MotionAmplificationJobId>(),
  );

  useEffect(() => {
    isMounted.current = true;
    const abortControllers = uploadAbortControllers.current;
    return () => {
      isMounted.current = false;
      for (const controller of abortControllers.values()) {
        controller.abort("navigation");
      }
    };
  }, []);

  function setUploadProgress(
    jobId: MotionAmplificationJobId,
    progress: number,
  ) {
    if (!isMounted.current) return;
    setActiveUploads((current) => new Map(current).set(jobId, progress));
  }

  function clearUploadProgress(jobId: MotionAmplificationJobId) {
    if (!isMounted.current) return;
    setActiveUploads(
      (current) => new Map([...current].filter(([id]) => id !== jobId)),
    );
  }

  function showPausedUploadDecision(submission: MotionAmplificationSubmission) {
    if (!submission.file) return;
    const session = submission.resumeSession;
    if (!session) return;
    showPausedMultipartUploadToast({
      jobId: session.jobId,
      jobName: submission.name,
      onResume: () => {
        void mutation.mutateAsync(submission).catch(() => undefined);
      },
      onExpire: () =>
        expirePausedMultipartUpload(session, () =>
          queryClient.invalidateQueries({
            queryKey: motionAmplificationJobsListKey,
          }),
        ),
    });
  }

  const mutation = useMutation({
    mutationKey: motionAmplificationUploadMutationKey,
    mutationFn: async (
      submission: MotionAmplificationSubmission,
    ): Promise<MotionAmplificationJobId> => {
      const {
        file,
        existingVideo,
        name,
        description,
        preset,
        parameters,
        fps,
        width,
        height,
        machineId,
        fileHandle,
        resumeSession,
        onUploadStarted,
      } = submission;
      if ((file === null) === (existingVideo === null)) {
        throw new Error(
          "Provide either a new video file or an existing video.",
        );
      }

      const fileName = existingVideo
        ? (existingVideo.fileName ?? "motion-amplification-video.mp4")
        : file.name;
      // Keep this key for every request made while handling this one submission.
      const idempotencyKey = createClientId();
      const savedSession =
        file && file.size >= MULTIPART_UPLOAD_THRESHOLD_BYTES
          ? (resumeSession ?? null)
          : null;
      if (resumeSession && !savedSession) {
        throw new Error("This upload can no longer be resumed.");
      }
      const upload = savedSession
        ? {
            jobId: savedSession.jobId,
            uploadUrl: null,
          }
        : await createMotionAmplificationUpload(
            {
              source: existingVideo ? "existing" : "upload",
              name: name.trim(),
              description: description.trim() || null,
              fileName,
              fileSize: existingVideo ? existingVideo.fileSize : file.size,
              contentType: existingVideo
                ? existingVideo.contentType
                : file.type || "video/mp4",
              rawObjectKey: existingVideo?.rawObjectKey,
              machineId: machineId ?? null,
              preset,
              ...(fps !== null ? { fps } : {}),
              ...(typeof width === "number" && typeof height === "number"
                ? { width, height }
                : {}),
              parameters,
            },
            idempotencyKey,
          );
      const jobId = upload.jobId;
      jobIdsBySubmission.current.set(submission, jobId);
      setUploadProgress(jobId, file ? 0 : 100);
      let uploadStartedNotified = false;
      function notifyUploadStarted() {
        if (uploadStartedNotified) return;
        uploadStartedNotified = true;
        onUploadStarted?.(jobId);
        void queryClient.invalidateQueries({
          queryKey: motionAmplificationJobsListKey,
        });
      }

      let startRequested = false;
      try {
        if (
          file &&
          file.size < MULTIPART_UPLOAD_THRESHOLD_BYTES &&
          !upload.uploadUrl
        ) {
          throw new Error("The upload URL was not returned by the API.");
        }
        if (file) {
          const abortController = new AbortController();
          uploadAbortControllers.current.set(jobId, abortController);
          try {
            if (file.size >= MULTIPART_UPLOAD_THRESHOLD_BYTES) {
              await uploadMultipartMotionAmplificationVideo(
                jobId,
                file,
                (progress) => setUploadProgress(jobId, progress),
                abortController.signal,
                savedSession ?? undefined,
                fileHandle,
                notifyUploadStarted,
              );
            } else if (upload.uploadUrl) {
              const uploadUrl = upload.uploadUrl;
              notifyUploadStarted();
              await retryTransientUploadFailures(
                jobId,
                () =>
                  uploadMotionAmplificationVideo(
                    uploadUrl,
                    file,
                    (progress) => setUploadProgress(jobId, progress),
                    { signal: abortController.signal },
                  ),
                abortController.signal,
              );
            }
          } finally {
            if (uploadAbortControllers.current.get(jobId) === abortController) {
              uploadAbortControllers.current.delete(jobId);
            }
          }
        }
        notifyUploadStarted();
        setUploadProgress(jobId, 100);
        startRequested = true;
        await startMotionAmplificationJob(jobId);
      } catch (uploadError: unknown) {
        const errorMessage =
          uploadError instanceof Error
            ? uploadError.message
            : "Unable to upload or start Motion Amplification.";
        // Once start has been requested, the BFF owns the job's terminal state.
        // Reporting here could overwrite or duplicate a failure it already stored.
        if (
          !startRequested &&
          !(uploadError instanceof MultipartUploadPausedError)
        ) {
          await reportMotionAmplificationJobFailure(jobId, {
            phase: "S3 upload",
            errorMessage,
          }).catch(() => undefined);
        }
        throw uploadError;
      }
      return jobId;
    },
    onSuccess: async (_jobId, submission) => {
      if (submission.file) {
        await queryClient.invalidateQueries({
          queryKey: motionAmplificationRawVideosQueryKey,
        });
      }
      toast.add("Motion amplification processing started.");
    },
    onError: (error, submission) => {
      if (error instanceof MultipartUploadPausedError && submission.file) {
        showPausedUploadDecision({
          ...submission,
          resumeSession: error.session ?? submission.resumeSession,
        });
        return;
      }
      toast.error(
        error instanceof Error
          ? error.message
          : "Unable to upload or start Motion Amplification.",
      );
    },
    onSettled: async (_jobId, _error, submission) => {
      await queryClient.invalidateQueries({
        queryKey: motionAmplificationJobsListKey,
      });
      // Until this refresh lands, the job's stale status would read as a paused or ready-to-start upload.
      const jobId = jobIdsBySubmission.current.get(submission);
      if (jobId) clearUploadProgress(jobId);
    },
  });

  return {
    ...mutation,
    uploadVideo: mutation.mutateAsync,
    activeUploads,
  };
}
